<script setup lang="ts">
import { computed, reactive } from "vue";
import UserAccount from "./UserAccount.vue";
import { useI18n } from "vue-i18n";
import { isMobileView, changeLanguage } from "../../utils/GeneralUtils.ts";
import AppHeader from "@persolog/ui/AppHeader.vue";
import LanguageSelection from "@persolog/ui/LanguageSelection.vue";
import ThemeSwitcher from "@persolog/ui/ThemeSwitcher.vue";
import UserManualButton from "../manual/UserManualButton.vue";
import { Trainer, Language } from "../../types/PersologOnlineTypes";
import { useTheme } from "../../composables/useTheme";
import { CUSTOMER_RELEASE_NOTES_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";
import { i18n, keycloak } from "../../main.ts";

const { t } = useI18n();
const { theme, toggleTheme } = useTheme();
const releaseNotesEnabled = useFeatureFlag(CUSTOMER_RELEASE_NOTES_FLAG);
const onLanguageChange = (lang: string) => changeLanguage(lang as Language);

const props = defineProps({
    highlight: {
        type: String,
        default: "",
    },
    trainer: {
        type: Object as () => Trainer | null,
        default: null,
    },
    skipLinkTarget: {
        type: String,
        default: "main-content",
    },
});

const loggedIn = !!keycloak.authenticated;

const items = reactive([
    {
        label: computed(() => t("header.navBar.profileOverview")),
        icon: "pi pi-fw pi-user",
        route: "/products",
        id: "products",
        visible: loggedIn,
    },
    {
        label: computed(() => t("header.navBar.projects")),
        icon: "pi pi-fw pi-briefcase",
        route: "/projects",
        id: "projects",
        visible: loggedIn,
    },
    {
        label: computed(() => t("header.navBar.shop")),
        icon: "pi pi-fw pi-external-link",
        url: "https://shop.persolog.com/auth/redirect/login?redirectTo=frontend.account.home.page&utm_source=persolog+online&utm_medium=anzeige",
        target: "_blank",
        id: "shop",
        visible: computed(
            () => props.trainer?.distributor.id == 1 || !props.trainer,
        ),
    },
    {
        label: computed(() => t("header.navBar.privacy")),
        icon: "pi pi-fw pi-lock",
        route: "/privacy",
        id: "privacy",
    },
    {
        // Two places to look for "what's new": the persolog blog (all platforms) and our own
        // release notes (this application). Kept as one menu entry so there is a single obvious spot.
        label: computed(() => t("home.news.subHeading")),
        icon: "pi pi-fw pi-megaphone",
        id: "news",
        items: [
            {
                label: computed(() => t("header.navBar.blog")),
                icon: "pi pi-fw pi-external-link",
                url: computed(() =>
                    i18n.global.locale.value === "de"
                        ? "https://persolog.com/de/blog/online-world-update/?utm_source=persolog.online&utm_medium=anzeige"
                        : "https://persolog.com/blog/online-world-update/?utm_source=persolog.online&utm_medium=anzeige",
                ),
                target: "_blank",
                id: "news-blog",
            },
            {
                label: computed(() => t("header.navBar.releaseNotes")),
                icon: "pi pi-fw pi-list",
                route: "/release-notes",
                id: "news-release-notes",
                // Hidden while the feature is off for this environment; the blog entry stays, so the
                // "news" menu never ends up empty.
                visible: computed(() => releaseNotesEnabled.value),
            },
        ],
    },
]);
</script>

<template>
    <AppHeader
        :items="items"
        :highlight="highlight"
        :skip-link-target="skipLinkTarget"
    >
        <template #start>
            <a href="/" v-if="!isMobileView()">
                <img
                    :alt="t('accessibility.images.logoPersolog')"
                    src="../../assets/persolog-logo.svg"
                    height="40"
                    class="h-10 w-auto mr-6"
                />
            </a>
        </template>
        <template #end>
            <div class="flex justify-between items-center">
                <a class="ml-2" href="/" v-if="isMobileView()">
                    <img
                        :alt="t('accessibility.images.logoPersolog')"
                        src="../../assets/persolog-logo.svg"
                        height="24"
                        class="h-6 w-auto mr-2 mt-2"
                    />
                </a>
                <div>
                    <div class="user-controls-container">
                        <UserAccount :trainer="trainer" />
                        <div class="user-manual-wrapper" v-if="!isMobileView()">
                            <UserManualButton />
                        </div>
                        <div class="theme-switcher-wrapper">
                            <ThemeSwitcher
                                :is-high-contrast="theme === 'high-contrast'"
                                @toggle="toggleTheme"
                            />
                        </div>
                        <div class="language-selector-wrapper">
                            <LanguageSelection @change="onLanguageChange" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </AppHeader>
</template>

<style scoped>
.user-controls-container {
    display: flex;
    align-items: center;
    position: relative;
}

.language-selector-wrapper {
    position: relative;
    margin-left: 1.5rem;
    margin-right: 1rem;
    margin-bottom: auto;
    margin-top: auto;
    width: 2.5rem;
}

@media screen and (max-width: 768px) {
    .language-selector-wrapper {
        margin-left: 0.5rem;
        margin-bottom: auto;
        margin-top: auto;
        margin-right: 0.75rem;
    }
}

.user-manual-wrapper {
    position: relative;
    margin-left: 2.5rem;
    margin-right: 1rem;
}

.theme-switcher-wrapper {
    position: relative;
    margin-left: 1.5rem;
    margin-right: 1rem;
}

@media screen and (max-width: 768px) {
    .theme-switcher-wrapper {
        margin-left: 0.5rem;
        margin-right: 0.5rem;
    }
}

@media screen and (max-width: 480px) {
    .sticky-menu {
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
    }
}
</style>
